import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Drawer, Box, Select, MenuItem, TextField, IconButton, Button, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';

import syncFindingsService from '../services/syncFindingsService';
import AdminUtils from '../services/utils';
import FindingDetail from '../components/syncFindings/findingDetail';
import { StatusChip, SourceChip } from '../components/syncFindings/statusBadge';

const DEFAULT_CRITERIA = {
  limit: 25,
  offset: 0,
  sort: { field: 'last_seen_at', direction: 'DESC' },
  search: {},
};

function StatTile({ label, value, color }) {
  return (
    <Box
      sx={{
        background: '#FFFFFF',
        border: '1px solid #E3E0EA',
        borderLeft: `3px solid ${color}`,
        borderRadius: '10px',
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        flex: 1,
      }}
    >
      <Typography variant="caption" sx={{ color: '#6B6578', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, fontSize: '11px' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '30px', fontWeight: 700, color: '#1F1B2E' }}>{value}</Typography>
    </Box>
  );
}

function SyncFindingsPage() {
  const [findings, setFindings] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [criteria, setCriteria] = useState(DEFAULT_CRITERIA);
  const [counts, setCounts] = useState({ needsReview: 0, errors: 0, autoCorrectedLast24h: 0, ignored: 0 });
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const getFindings = async () => {
    const result = await syncFindingsService.getFindings(criteria);
    setFindings(result.findings || []);
    setTotalRows(result.totalCount || 0);
  };

  const getCounts = async () => {
    setCounts(await syncFindingsService.getCounts());
  };

  const refresh = async () => {
    await Promise.all([getFindings(), getCounts()]);
  };

  useEffect(() => {
    refresh();
  }, [criteria]);

  const updateSearch = (field, value) => {
    setCriteria((prev) => {
      const search = { ...prev.search };
      if (value) {
        search[field] = value;
      } else {
        delete search[field];
      }
      return { ...prev, search, offset: 0 };
    });
  };

  const handlePaginationChange = (model) => {
    setPaginationModel(model);
    setCriteria((prev) => ({ ...prev, limit: model.pageSize, offset: model.page * model.pageSize }));
  };

  const handleSortChange = (model) => {
    if (model.length > 0) {
      const { field, sort } = model[0];
      setCriteria((prev) => ({ ...prev, sort: { field, direction: sort === 'asc' ? 'ASC' : 'DESC' } }));
    }
  };

  const handleEyeClick = (finding) => {
    setSelectedFinding(finding);
    setIsPanelOpen(true);
  };

  const handleUpdated = async () => {
    setIsPanelOpen(false);
    await refresh();
  };

  const columns = [
    {
      field: 'actions',
      headerName: '',
      width: 55,
      sortable: false,
      renderCell: (params) => (
        <IconButton onClick={() => handleEyeClick(params.row)} title="View">
          <FontAwesomeIcon className="purpleText" icon={faEye} style={{ fontSize: '15px' }} />
        </IconButton>
      ),
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 100,
      renderCell: (params) => <SourceChip source={params.row.source} />,
    },
    { field: 'kind', headerName: 'Kind', width: 220 },
    {
      field: 'user_email',
      headerName: 'User',
      width: 200,
      renderCell: (params) => params.row.user_email || <span style={{ color: '#9CA3AF' }}>No local user</span>,
    },
    { field: 'detail', headerName: 'Detail', flex: 1, minWidth: 260 },
    {
      field: 'last_seen_at',
      headerName: 'Last Seen',
      width: 180,
      renderCell: (params) => {
        const lastSeen = params.row.last_seen_at || params.row.detected_at;
        const count = params.row.occurrence_count || 1;
        return (
          <span>
            {lastSeen ? AdminUtils.FormatDateWithTime(lastSeen) : ''}
            {count > 1 ? ` (x${count})` : ''}
          </span>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => <StatusChip finding={params.row} />,
    },
  ];

  return (
    <div style={{ margin: '20px' }}>
      <Drawer anchor="right" open={isPanelOpen} onClose={() => setIsPanelOpen(false)} sx={{ '& .MuiDrawer-paper': { width: '700px' } }}>
        {selectedFinding && (
          <FindingDetail finding={selectedFinding} onClose={() => setIsPanelOpen(false)} onUpdated={handleUpdated} />
        )}
      </Drawer>

      <Typography variant="h5" sx={{ fontWeight: 700, marginBottom: '4px' }}>
        Error Review
      </Typography>
      <Typography variant="body2" sx={{ color: '#6B6578', marginBottom: '20px' }}>
        Findings from reconciliation jobs and in-app failures that could not be auto-corrected.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, marginBottom: 3 }}>
        <StatTile label="Needs Review" value={counts.needsReview} color="#F59E0B" />
        <StatTile label="Auto-Corrected (24h)" value={counts.autoCorrectedLast24h} color="#22C55E" />
        <StatTile label="Ignored" value={counts.ignored} color="#9CA3AF" />
        <StatTile label="Errors" value={counts.errors} color="#E11D48" />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', marginBottom: 2, flexWrap: 'wrap' }}>
        <Select
          size="small"
          value={criteria.search.source || ''}
          onChange={(e) => updateSearch('source', e.target.value)}
          displayEmpty
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">Source: All</MenuItem>
          <MenuItem value="stripe">Stripe</MenuItem>
          <MenuItem value="wix">Wix</MenuItem>
          <MenuItem value="user">User</MenuItem>
        </Select>
        <Select
          size="small"
          value={criteria.search.status || ''}
          onChange={(e) => updateSearch('status', e.target.value)}
          displayEmpty
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Status: All</MenuItem>
          <MenuItem value="open">Needs Review</MenuItem>
          <MenuItem value="resolved">Resolved</MenuItem>
          <MenuItem value="ignored">Ignored</MenuItem>
        </Select>
        <TextField
          size="small"
          placeholder="Search by user, email, or Stripe ID"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && updateSearch('q', searchText)}
          sx={{ minWidth: 300 }}
        />
        <Button variant="contained" onClick={() => updateSearch('q', searchText)}>
          Apply Filters
        </Button>
      </Box>

      <DataGrid
        rows={findings}
        columns={columns}
        rowCount={totalRows}
        paginationMode="server"
        sortingMode="server"
        pageSizeOptions={[10, 25, 50]}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        onSortModelChange={handleSortChange}
        sx={{ marginTop: 2 }}
      />
    </div>
  );
}

export default SyncFindingsPage;
